import { useCallback, useEffect, useRef, useState } from 'react';

export type MidiStatus = 'idle' | 'checking' | 'connected' | 'disconnected' | 'unsupported' | 'denied';

export interface MidiHit {
  drumId: string;
  note: number;
  velocity: number;
  at: number;
}

export interface MidiRawNote {
  note: number;
  velocity: number;
  drumId: string | null;
  at: number;
}

const MIDI_NOTE_MAP = new Map<number, string>([
  [57, 'crash'],
  [50, 'tom1'],
  [47, 'tom2'],
  [51, 'ride'],
  [38, 'snare'],
  [42, 'hihat'],
  [41, 'floor'],
  [35, 'kick'], [36, 'kick'],
]);

export const MIDI_DRUM_INDEX: Record<string, number> = {
  crash: 0,
  tom1: 1,
  tom2: 2,
  ride: 3,
  snare: 4,
  hihat: 5,
  floor: 6,
  kick: 7,
};

function isDenied(error: any) {
  return error?.name === 'SecurityError' || error?.name === 'NotAllowedError';
}

export function useMidiDrums(enabled: boolean) {
  const [status, setStatus] = useState<MidiStatus>('idle');
  const [deviceName, setDeviceName] = useState('');
  const [lastHit, setLastHit] = useState<MidiHit | null>(null);
  const [lastRawNote, setLastRawNote] = useState<MidiRawNote | null>(null);
  const accessRef = useRef<any>(null);
  const scanIdRef = useRef(0);

  const bindInputs = useCallback((access: any) => {
    const inputs = Array.from(access.inputs.values()) as any[];
    const openResults = inputs.map((input) => {
      input.onmidimessage = (event: any) => {
        const [command, note, velocity] = event.data || [];
        if ((command & 0xf0) !== 0x90 || !velocity) return;
        const drumId = MIDI_NOTE_MAP.get(note);
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.log('[midi]', { note, velocity, mappedTo: drumId || 'UNMAPPED' });
        }
        setLastRawNote({ note, velocity, drumId: drumId || null, at: performance.now() });
        if (!drumId) return;
        setLastHit({ drumId, note, velocity, at: performance.now() });
      };
      if (typeof input.open === 'function') {
        return input.open().catch(() => undefined);
      }
      return Promise.resolve();
    });
    Promise.all(openResults).then(() => {
      const connectedInputs = inputs.filter(
        (input) => input.state !== 'disconnected' && input.connection === 'open',
      );
      setStatus(connectedInputs.length ? 'connected' : 'disconnected');
      setDeviceName(connectedInputs.map((input) => input.name).filter(Boolean).join(', '));
    });
  }, []);

  const attachAccess = useCallback((access: any) => {
    accessRef.current = access;
    access.onstatechange = () => bindInputs(access);
    bindInputs(access);
  }, [bindInputs]);

  const scan = useCallback(async () => {
    if (!enabled) return;
    const scanId = ++scanIdRef.current;
    if (typeof (navigator as any).requestMIDIAccess !== 'function') {
      setStatus('unsupported');
      setDeviceName('');
      return;
    }
    setStatus('checking');
    try {
      const access = await (navigator as any).requestMIDIAccess({ sysex: false });
      if (scanId !== scanIdRef.current) return;
      attachAccess(access);
    } catch (error: any) {
      if (scanId !== scanIdRef.current) return;
      setStatus(isDenied(error) ? 'denied' : 'disconnected');
      setDeviceName('');
    }
  }, [attachAccess, enabled]);

  useEffect(() => {
    if (!enabled) {
      setStatus('idle');
      setDeviceName('');
      return undefined;
    }
    scan();
    const refresh = () => {
      if (accessRef.current) {
        bindInputs(accessRef.current);
      } else {
        scan();
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      scanIdRef.current += 1;
      if (accessRef.current) accessRef.current.onstatechange = null;
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return { status, deviceName, lastHit, lastRawNote, scan };
}
